import { Directive, ElementRef, HostListener, Input, OnDestroy, OnInit, Renderer2 } from '@angular/core';
import { NgControl, UntypedFormControl } from '@angular/forms';
import { delay, take, takeUntil } from 'rxjs/operators';
import { CustomKeyboardEvent } from 'ngx-mask';
import { ReplaySubject, Subscription, timer } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { GenericService } from '@services/generic.service';
import { UtilsHelper } from '@helpers/utils-helper';

@Directive({
  selector: '[appMaskErrorTooltip]',
})
export class MaskErrorTooltipDirective implements OnInit, OnDestroy {
  @Input() public maxMaskLength: number;
  @Input() public maxLengthMessage: string;
  @Input() public maskSkipEnter: boolean;
  @Input() public setZIndexHigherThanModal: boolean = false;
  @Input('appMaskErrorTooltip') public message: string = 'MASK_ERROR.ONLY_ENGLISH';

  private hideTime: number = 3000;
  private formControl: UntypedFormControl;
  private prevInputValue: string;
  private prevControlValue: string;
  private lastEnteredValue: string;
  private isSkipEnter: boolean;

  private focused: boolean = false;
  private tooltip: HTMLElement;
  private arrow: HTMLElement;
  private tooltipInner: HTMLElement;
  private messageNode: HTMLElement;
  private hideTooltipTimer: Subscription;
  private showTooltipTimer: Subscription;
  private isDestroy: ReplaySubject<boolean> = new ReplaySubject<boolean>();

  constructor(
    private control: NgControl,
    private elementRef: ElementRef,
    private renderer: Renderer2,
    private translateService: TranslateService,
    private genericService: GenericService,
  ) {}

  public ngOnInit(): void {
    this.genericService.resize$.pipe(takeUntil(this.isDestroy)).subscribe(() => {
      this.setTooltipPosition();
    });

    this.genericService.wheel$.pipe(takeUntil(this.isDestroy)).subscribe(() => {
      this.hideTooltip();
    });

    this.formControl = this.control.control as UntypedFormControl;

    this.prevInputValue = this.elementRef?.nativeElement?.value;
    this.prevControlValue = this.formControl?.value;

    if (!!this.formControl) {
      this.formControl.valueChanges.pipe(takeUntil(this.isDestroy), delay(100)).subscribe((currentControlValue) => {
        this.checkChanges(currentControlValue, this.elementRef.nativeElement.value);
      });
    }
  }

  @HostListener('focus', ['$event'])
  public onFocus() {
    this.focused = true;
  }

  @HostListener('blur', ['$event'])
  public onBlur() {
    this.focused = false;
    this.hideTooltip();
  }

  @HostListener('keydown', ['$event'])
  public onKeydown(e) {
    this.isSkipEnter = e.code === 'Enter';
  }

  public ngOnDestroy(): void {
    this.destroyTooltip();
    this.clearHideTooltipTimer();
    this.clearShowTooltipTimer();
    this.isDestroy.next(true);
    this.isDestroy.complete();
  }

  @HostListener('input', ['$event'])
  public onInput(e: CustomKeyboardEvent): void {
    this.lastEnteredValue = (e as any).data || null;
    if (UtilsHelper.isNull(this.lastEnteredValue)) {
      this.hideTooltip();
    }
  }

  private checkChanges(currentControlValue: string, currentInputValue: string) {
    if (!UtilsHelper.isUndefined(this.lastEnteredValue)) {
      if (
        (currentInputValue || null) === (this.prevInputValue || null) &&
        (currentControlValue || null) === (this.prevControlValue || null) &&
        this.focused &&
        !this.isSkipEnter
      ) {
        this.showTooltip(
          this.maxMaskLength && currentControlValue && currentControlValue.length === this.maxMaskLength && !!this.lastEnteredValue,
        );
      } else {
        this.hideTooltip();
      }
      this.prevInputValue = this.elementRef?.nativeElement?.value;
      this.prevControlValue = currentControlValue;
    }
    this.lastEnteredValue = undefined;
  }

  private showTooltip(isMaxLength: boolean) {
    const message: string = this.getMessage(isMaxLength);
    if (!message) {
      return;
    }
    if (!this.tooltip) {
      this.renderTooltip(message);
    } else {
      this.setTooltipPosition();
      this.updateMessage(message);
    }
    this.clearShowTooltipTimer();
    this.showTooltipTimer = timer(0)
      .pipe(takeUntil(this.isDestroy))
      .subscribe(() => {
        this.renderer.addClass(this.tooltip, 'show');
        this.clearHideTooltipTimer();
        this.hideTooltipTimer = timer(this.hideTime).subscribe(() => {
          this.hideTooltip();
        });
      });
  }

  private getMessage(isMaxLength: boolean): string {
    return isMaxLength ? this.maxLengthMessage || 'MASK_ERROR.MAX_LENGTH' : this.message;
  }

  private updateMessage(message: string) {
    this.translateService
      .get(message, { maxLength: this.maxMaskLength })
      .pipe(take(1))
      .subscribe((translatedMessage) => {
        this.renderer.removeChild(this.tooltipInner, this.messageNode);
        this.messageNode = this.renderer.createText(translatedMessage);
        this.renderer.appendChild(this.tooltipInner, this.messageNode);
      });
  }

  private renderTooltip(message: string) {
    this.translateService
      .get(message, { maxLength: this.maxMaskLength })
      .pipe(take(1))
      .subscribe((translatedMessage) => {
        this.tooltip = this.renderer.createElement('div');
        this.renderer.addClass(this.tooltip, 'tooltip');
        this.renderer.addClass(this.tooltip, 'bs-tooltip-bottom');
        this.renderer.addClass(this.tooltip, 'error');
        this.renderer.addClass(this.tooltip, 'fade');
        this.renderer.addClass(this.tooltip, 'position-fixed');
        this.renderer.setStyle(this.tooltip, 'pointer-events', 'none');
        if (this.setZIndexHigherThanModal) {
          this.renderer.setStyle(this.tooltip, 'z-index', '10000');
        }
        this.setTooltipPosition();
        this.renderer.setStyle(this.tooltip, 'transform', 'translateX(-50%)');
        this.renderer.setAttribute(this.tooltip, 'role', 'tooltip');
        const id: string = this.elementRef.nativeElement.id ? this.elementRef.nativeElement.id : 'custom';
        this.renderer.setAttribute(this.tooltip, 'id', 'error-tooltip-' + id);

        this.arrow = this.renderer.createElement('div');
        this.renderer.addClass(this.arrow, 'arrow');
        this.renderer.setStyle(this.arrow, 'left', '50%');
        this.renderer.setStyle(this.arrow, 'transform', 'translateX(-50%)');
        this.renderer.appendChild(this.tooltip, this.arrow);

        this.tooltipInner = this.renderer.createElement('div');
        this.renderer.addClass(this.tooltipInner, 'tooltip-inner');
        this.renderer.appendChild(this.tooltip, this.tooltipInner);

        this.messageNode = this.renderer.createText(translatedMessage);
        this.renderer.appendChild(this.tooltipInner, this.messageNode);

        this.renderer.appendChild(this.body, this.tooltip);
      });
  }

  private hideTooltip() {
    if (this.tooltip) {
      this.renderer.removeClass(this.tooltip, 'show');
      this.clearHideTooltipTimer();
    }
  }

  private clearShowTooltipTimer() {
    if (this.showTooltipTimer) {
      this.showTooltipTimer.unsubscribe();
      this.showTooltipTimer = null;
    }
  }

  private clearHideTooltipTimer() {
    if (this.hideTooltipTimer) {
      this.hideTooltipTimer.unsubscribe();
      this.hideTooltipTimer = null;
    }
  }

  private destroyTooltip() {
    if (this.elementRef.nativeElement && this.tooltip) {
      this.renderer.removeChild(this.body, this.tooltip);
    }
  }

  private setTooltipPosition() {
    if (!this.tooltip || !this.elementRef || !this.elementRef.nativeElement) {
      return;
    }
    const coords = this.elementRef.nativeElement.getBoundingClientRect();
    const elemRendered: boolean = !!(coords.height && coords.width);
    if (!elemRendered) {
      this.hideTooltip();
      return;
    }
    this.renderer.setStyle(this.tooltip, 'top', coords.top + coords.height + 'px');
    this.renderer.setStyle(this.tooltip, 'left', coords.left + coords.width / 2 + 'px');
  }

  private get body(): HTMLElement {
    return document.getElementsByTagName('body')[0];
  }
}
